import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { Page } from '../../../types';
import { PageSlotCard } from './PageSlotCard';
import { PAGE_CONFIG } from './constants';

interface PageSlot {
  id: string;
  row: number;
  column: string;
  page?: Page;
  isEmpty: boolean;
}

interface PageGridProps {
  visibleRows: number[];
  visibleColumns: string[];
  getSlotsByRow: (row: number) => PageSlot[];
  uploadingSlots: Set<string>;
  draggedSlot: PageSlot | null;
  onDragStart: (e: React.DragEvent, slot: PageSlot) => void;
  onDrop: (e: React.DragEvent, slot: PageSlot) => void;
  onDragEnd: () => void;
  onDeletePage: (slot: PageSlot) => void;
  onEditPage: (page: Page) => void;
  onImageSelect: (slotId: string, event: React.MouseEvent<HTMLElement>) => void;
  onImageDrop: (e: React.DragEvent, slotId: string) => void;
  onImageDragOver: (e: React.DragEvent) => void;
  onAddRow?: () => void;
  onAddColumn?: () => void;
  maxRows?: number;
  maxColumns?: number;
}

export const PageGrid: React.FC<PageGridProps> = ({
  visibleRows,
  visibleColumns,
  getSlotsByRow,
  uploadingSlots,
  draggedSlot,
  onDragStart,
  onDrop,
  onDragEnd,
  onDeletePage,
  onEditPage,
  onImageSelect,
  onImageDrop,
  onImageDragOver,
  onAddRow,
  onAddColumn,
  maxRows = 1,
  maxColumns = 1,
}) => {
  const canAddRow = maxRows < PAGE_CONFIG.MAX_ROWS && onAddRow;
  const canAddColumn = maxColumns < PAGE_CONFIG.MAX_COLUMNS && onAddColumn;

  // 細長い追加ボタン（列追加用：全行分の高さ、ヘッダー行を除く）
  const AddColumnButton: React.FC<{ onClick: () => void; rowCount: number }> = ({ onClick, rowCount }) => {
    // ヘッダー行の高さ（60px固定）
    const headerHeight = 60;
    // 各行の高さ + gap
    const rowHeight = 292;
    const gap = 16;
    // 全行分の高さ（ヘッダー行を除く）
    const totalHeight = rowCount * rowHeight + (rowCount - 1) * gap;
    
    return (
      <Card
        onClick={onClick}
        sx={{
          width: 80, // 細長くするため幅を狭く
          height: totalHeight,
          cursor: 'pointer',
          border: '2px dashed #d0d0d0',
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 1,
          boxShadow: 'none',
          backgroundColor: '#ffffff',
          transition: 'background-color 0.2s, border-color 0.2s',
          '&:hover': {
            backgroundColor: '#f5f5f5',
            borderColor: '#999999'
          }
        }}
      >
        <CardContent sx={{ p: 0, height: '100%', position: 'relative', '&:last-child': { pb: 0 } }}>
          <Box
            sx={{
              height: '100%',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* 中央のプラスアイコン */}
            <AddIcon sx={{ fontSize: '2rem', color: 'text.secondary' }} />
          </Box>
        </CardContent>
      </Card>
    );
  };

  // 細長い追加ボタン（行追加用：全列分の幅）
  const AddRowButton: React.FC<{ onClick: () => void; columnCount: number }> = ({ onClick, columnCount }) => (
    <Card
      onClick={onClick}
      sx={{
        width: columnCount * 168 + (columnCount - 1) * 16, // 全列の幅 + 列間のgap
        height: 80, // 細長くするため高さを狭く
        cursor: 'pointer',
        border: '2px dashed #d0d0d0',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 1,
        boxShadow: 'none',
        backgroundColor: '#ffffff',
        transition: 'background-color 0.2s, border-color 0.2s',
        '&:hover': {
          backgroundColor: '#f5f5f5',
          borderColor: '#999999'
        }
      }}
    >
      <CardContent sx={{ p: 0, height: '100%', position: 'relative', '&:last-child': { pb: 0 } }}>
        <Box
          sx={{
            height: '100%',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* 中央のプラスアイコン */}
          <AddIcon sx={{ fontSize: '2rem', color: 'text.secondary' }} />
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box 
      sx={{ 
        width: '100%',
        border: '1px solid #e0e0e0',
        borderRadius: '12px',
        p: 2,
        backgroundColor: 'white',
        overflowX: 'auto'
      }}
    >
      <Box>
        {/* メイングリッドと右側の列追加ボタン */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          {/* メイングリッド */}
          <Box 
            sx={{ 
              display: 'grid', 
              gridTemplateColumns: `60px repeat(${visibleColumns.length}, 168px)`,
              gap: 2,
              minWidth: 'fit-content'
            }}
          >
            {/* ヘッダー行 */}
            <Box></Box>
            {visibleColumns.map(column => (
              <Box 
                key={column}
                textAlign="center" 
                sx={{ 
                  height: 60, // 行番号と同じ高さに固定
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography variant="body2" fontWeight="bold">{column}</Typography>
              </Box>
            ))}

            {/* 各行（動的に表示） */}
            {visibleRows.map(row => (
              <React.Fragment key={row}>
                {/* 行番号 */}
                <Box 
                  display="flex" 
                  alignItems="center" 
                  justifyContent="center"
                  sx={{ 
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1,
                    height: 292, // フロント側と同じ比率 (1000:1740)
                    fontSize: '1.2rem',
                    fontWeight: 'normal'
                  }}
                >
                  {row}
                </Box>
                
                {/* A, B, C列のスロット */}
                {getSlotsByRow(row).map(slot => (
                  <PageSlotCard 
                    key={slot.id} 
                    slot={slot}
                    isUploading={uploadingSlots.has(slot.id)}
                    isDragging={draggedSlot?.id === slot.id}
                    onDragStart={onDragStart}
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                        e.dataTransfer.dropEffect = 'copy';
                      } else {
                        e.dataTransfer.dropEffect = 'move';
                      }
                    }}
                    onDrop={onDrop}
                    onDragEnd={onDragEnd}
                    onDelete={onDeletePage}
                    onEdit={onEditPage}
                    onImageSelect={onImageSelect}
                    onImageDrop={onImageDrop}
                    onImageDragOver={onImageDragOver}
                  />
                ))}
              </React.Fragment>
            ))}
          </Box>

          {/* 右側の列追加ボタン（全行分の高さ） */}
          {canAddColumn && (
            <Box 
              sx={{ 
                display: 'flex',
                flexDirection: 'column',
                alignSelf: 'stretch',
                mt: '76px', // ヘッダー行の高さ（60px）+ gap（16px）
              }}
            >
              <AddColumnButton onClick={onAddColumn} rowCount={visibleRows.length} />
            </Box>
          )}
        </Box>

        {/* 下側の行追加ボタン（全列分の幅） */}
        {canAddRow && (
          <Box 
            sx={{ 
              display: 'flex',
              mt: 2,
              pl: '76px', // 行番号列の幅（60px）+ gap（16px）
            }}
          >
            <AddRowButton onClick={onAddRow} columnCount={visibleColumns.length} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

